/* eslint-disable react/prop-types */
/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import { css, jsx } from '@emotion/core'
import PropTypes from 'prop-types'
import Icon from '@material-ui/core/Icon'
import CircularProgress from '@material-ui/core/CircularProgress'
import Tooltip from '@material-ui/core/Tooltip'
import SurfaceJob from '../SurfaceJob'
import { theme, Status, GAMES_DATASET_DATA_TYPE } from '../../../../constants'
import { confirmAlert } from '../../../notifications/Actions'

const grey = theme.palette.common.greyHover

const styles = {
  itemContainer: css`
    display: flex;
    height: 38px;
    margin-right: 7px;
    outline: 0;
    cursor: default;
    &:hover {
      .video-menu-item-content {
        background: ${grey};
      }
    }
  `,
  icon: css`
    width: 30px;
    height: 100%;
  `,
  content: css`
    flex: 1;
    display: flex;
    margin-left: 30px;
    padding: 0 7px;
  `,
  name: css`
    display: flex;
    flex: 1;
    font-size: 13px;
    align-items: center;
    padding-right: 15px;
    margin-left: 30px;
  `,
  icons: css`
    display: flex;
    align-items: center;
  `,
  iconTag: css`
    font-size: 17px;
    margin-left: 10px;
  `,
  iconButton: css`
    cursor: pointer;
    &:hover {
      opacity: 0.75;
    }
  `,
  innerIcon: css`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  logoImg: css`
    width: 18px;
  `,
  disableIcon: css`
    color: lightgrey;
    pointer-events: none;
    cursor: not-allowed;
  `
}

function shouldDisableItem(surfaceState, props) {
  const dataType = props.vp.data_type
  if (
    surfaceState.deletingVp ||
    dataType === GAMES_DATASET_DATA_TYPE.AUTOMATIC ||
    !props.isProcessSurfaceAvailable
  ) {
    return true
  }

  return false
}

function PitchControlMenuItem(props) {
  const [showStartTooltip, setShowStartTooltip] = React.useState(false)
  const [hideStartTooltip, setHideStartTooltip] = React.useState(false)

  const tooltipMessage =
    (props.vp.data_type === GAMES_DATASET_DATA_TYPE.AUTOMATIC &&
      window.I18n.t('surfaces.automaticVp')) ||
    (!props.isProcessSurfaceAvailable && window.I18n.t('surfaces.needsAtd')) ||
    ''

  const startJob = props.processSurface
  const isRunning = props.isSurfaceRunning

  return (
    <SurfaceJob
      vp={props.vp}
      surfaceJob={props.surfaceJob}
      isVpCreatedInCloud={props.isVpCreatedInCloud}
      deletingVp={props.deletingVp}
      render={surfaceState => {
        const disabledItem = shouldDisableItem(surfaceState, props)

        return (
          <div
            css={css`
              ${styles.itemContainer};
            `}
          >
            <Tooltip
              title={tooltipMessage}
              placement="top"
              open={showStartTooltip && !hideStartTooltip}
              onOpen={() => setShowStartTooltip(true)}
              onClose={() => setShowStartTooltip(false)}
            >
              <div css={styles.content} className="video-menu-item-content">
                <div
                  css={css`
                    ${styles.name};
                    color: ${disabledItem ? 'lightgrey' : theme.palette.common.black};
                  `}
                >
                  {window.I18n.t('surfaces.titlePitchControl')}
                  <Tooltip
                    title={window.I18n.t('surfaces.pitchControl')}
                    placement="left"
                    onOpen={() => setHideStartTooltip(true)}
                    onClose={() => setHideStartTooltip(false)}
                  >
                    <Icon
                      style={{
                        color: 'grey',
                        fontSize: '18px',
                        marginLeft: '10px',
                        top: '6px',
                        left: '5px',
                        transform: 'rotate(5deg)'
                      }}
                    >
                      info
                    </Icon>
                  </Tooltip>
                </div>
                <div css={styles.icons}>
                  {surfaceJobUI(props, isRunning, surfaceState, startJob)}
                </div>
              </div>
            </Tooltip>
          </div>
        )
      }}
    />
  )
}

function surfaceJobUI(props, isRunning, surfaceState, startJob) {
  if (isRunning) {
    return <RunningUi vpId={props.vp._id} cancelSurface={props.cancelSurface} />
  }

  if (surfaceState.hasSurface) {
    return done()
  }

  if (!props.vp.files.video) {
    return null
  }

  if (!props.vp.compute_surfaces) {
    return startUI(props, startJob)
  }

  if (props.surfaceJob) {
    const surfaceJob = props.surfaceJob
    const status = surfaceJob && surfaceJob.status

    switch (status) {
      case 'CHECKING':
      case Status.QUEUED_LOCALLY:
      case Status.QUEUED:
      case Status.STARTING:
      case Status.ACTIVE:
      case Status.FINISHING:
      case Status.CONNECTIONFAILED:
        return (
          <Tooltip
            title={window.I18n.t('surfaces.pitchControlProcessing')}
            enterDelay={400}
            placement="top"
          >
            <CircularProgress
              css={css`
                ${styles.iconTag};
              `}
              size={14}
              thickness={4}
              color="primary"
            />
          </Tooltip>
        )
      case Status.FAILED:
        return errorUI(props, startJob)
      case Status.COMPLETE: // In this case is complete, but doesnt have surfaces
      case Status.CANCEL:
        return startUI(props, startJob)
      default:
        return null
    }
  }

  if (surfaceState.showStartSurface) {
    return startUI(props, startJob)
  }

  if (surfaceState.errorTryAgain) {
    return errorUI(props, startJob)
  }

  return null
}

function done() {
  return (
    <React.Fragment>
      <Tooltip
        title={window.I18n.t('surfaces.pitchControlProcessed')}
        enterDelay={400}
        placement="top"
      >
        <Icon
          css={css`
            ${styles.iconTag};
            color: ${theme.palette.common.successGreen};
          `}
        >
          check_circle_outline
        </Icon>
      </Tooltip>
    </React.Fragment>
  )
}

function errorUI(props, startJob) {
  const isEnabled = props.errorTrackingLinkAvailable && props.isProcessSurfaceAvailable
  if (props.isMetricaSportsUser) {
    return (
      <span
        css={css`
          ${styles.iconTag};
          ${styles.iconButton};
          color: ${theme.palette.error.main};
          font-size: 12px;
          text-decoration: underline;
          ${isEnabled ? '' : styles.disableIcon};
        `}
        // eslint-disable-next-line
        onClick={() => {
          if (isEnabled) {
            startJob()
          }
        }}
      >
        {window.I18n.t('common.errorTryAgain')}
      </span>
    )
  }

  return (
    <span
      css={css`
        ${styles.iconTag};
        color: ${theme.palette.error.main};
        font-size: 12px;
        ${isEnabled ? '' : styles.disableIcon};
      `}
    >
      {window.I18n.t('admin.errorContactSupport')}
    </span>
  )
}

function startUI(props, startJob) {
  const tooltipMessage =
    (!props.isProcessSurfaceAvailable && window.I18n.t('surfaces.needsAtd')) || ''
  return (
    <Tooltip title={tooltipMessage} placement="top">
      <span
        css={css`
          ${styles.iconTag};
          ${styles.iconButton};
          color: ${theme.palette.common.blue};
          font-size: 12px;
          text-decoration: underline;
          ${props.isProcessSurfaceAvailable ? '' : styles.disableIcon};
        `}
        // eslint-disable-next-line
        onClick={() => {
          if (props.isProcessSurfaceAvailable) {
            startJob()
          }
        }}
      >
        {window.I18n.t('common.start')}
      </span>
    </Tooltip>
  )
}

class RunningUi extends React.PureComponent {
  state = {
    isHovered: false
  }

  setHover = isHovered => () => {
    this.setState({ isHovered })
  }

  cancelProcess = () => {
    confirmAlert()
      .then(() => {
        this.props.cancelSurface(this.props.vpId)
      })
      .catch(() => {})
  }

  render() {
    return (
      <Tooltip title={window.I18n.t('admin.cancelProcessTooltip')} enterDelay={400} placement="top">
        {!this.state.isHovered ? (
          <CircularProgress
            css={css`
              ${styles.iconTag};
            `}
            size={14}
            thickness={4}
            color="primary"
            onMouseEnter={this.setHover(true)}
            onMouseLeave={this.setHover(false)}
          />
        ) : (
          <Icon
            css={css`
              ${styles.iconTag};
              ${styles.iconButton};
              color: ${theme.palette.error.main};
            `}
            onMouseLeave={this.setHover(false)}
            onClick={this.cancelProcess}
          >
            cancel
          </Icon>
        )}
      </Tooltip>
    )
  }
}

PitchControlMenuItem.propTypes = {
  vp: PropTypes.object.isRequired,
  isVpCreatedInCloud: PropTypes.bool.isRequired,
  surfaceJob: PropTypes.object, // eslint-disable-line
  isSurfaceRunning: PropTypes.bool.isRequired,
  deletingVp: PropTypes.bool.isRequired,
  processSurface: PropTypes.func.isRequired, // eslint-disable-line
  cancelSurface: PropTypes.func.isRequired,
  isProcessSurfaceAvailable: PropTypes.bool.isRequired // eslint-disable-line
}

export default PitchControlMenuItem
